// src/oura/components/PlanEditor.tsx
//
// "The plan" card on the staff Contract tab (plan table spec v1.1, 29 Sep
// 2026): the signed plan, one line per deliverable, grouped by when it
// happens. Month 1's Foundation (split into Measure, Fix the site, Write,
// Google and listings, Hand over), the lines that run every month, then the
// milestones. A line the rules do not know (a custom X code, a line moved
// out of its months) sits in its time group's "Other", and is never lost.
//
// The list only. It does not fetch and it does not edit: ContractTab owns
// the record, the draft, the line drawer and the save bar, and passes the
// grouped view in. A row opens the line's drawer, where the label, months
// and quantity are edited. Each group head says where it sits in time, open
// or folded: "This month" (open), "Due by Thu 17 Sep" or "Next: month 6"
// (folded, with anything unsaved or to fix still said on the head).
//
// Each row says how the line is proven, as one small mark: "Site scan checks
// it" (a site scan reads its checks) or "We confirm it" (the team reports it).
// The key line at the top is the legend, and the drawer says it in full.
//
// A row is one line: the mark, what the client sees with its code chip right
// after it (as on the Documents board), and one fact on the right. The fact
// rides in the row's tags slot rather than its meta, so under 36rem it drops
// under the label with the row's other parts instead of squeezing the label
// to a word.

import type { ReactNode } from "react";
import { CalendarIcon, PenLineIcon, PlusIcon, XIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { dayDate, shortDateOrNull } from "../lib/oura";
import {
    AREA_TEXT,
    FOCUS_RING,
    FamilyChip,
    FoldCard,
    GroupFold,
    LATE_TEXT,
    ListRow,
    PillButton,
    ShowMore,
    TAP_TARGET,
    TYPE,
    Tag,
    type Tone,
} from "@oura/ui";
import type { EngagementChange } from "@service/aeo/clientReview";
import { CoversIcon, CoversMark } from "./plan/CoversMark";
import { monthEndDay } from "./plan/planDates";
import {
    BY_HAND_WORDS,
    COVERED_WORDS,
    familyOf,
    groupCounts,
    groupStartsOpen,
    isCovered,
    rowLate,
    rowMonthsFact,
    rowQuantity,
    shortQuantity,
    type PageGap,
    type TimeGroup,
    type ViewGroup,
    type ViewRow,
} from "./plan/planModel";

function plural(n: number, one: string, many = `${one}s`): string {
    return `${n} ${n === 1 ? one : many}`;
}

/** Parts joined with " · ", each its own node so one can carry its own colour. */
function dotted(parts: ReactNode[]): ReactNode {
    return parts.map((p, i) => (
        <span key={i}>
            {i > 0 && " · "}
            {p}
        </span>
    ));
}

export interface PlanCardProps {
    groups: ViewGroup[];
    lineCount: number;
    counts: { checked: number; confirmed: number };
    term: number;
    month: number;
    notStarted: boolean;
    /** The start date as it stands in the draft, for the "Ended" dates. */
    start: string;
    /** Unsaved changes, for the folded card. */
    changes: number;
    /** Lines the server refused on the last save. */
    refused: ReadonlySet<string>;
    /** The server's warnings after the last save. */
    warnings: string[];
    onDismissWarnings: () => void;
    changeLog: EngagementChange[];
    /** The page sum the server refuses a save on, when the lines do not add up to the contract. */
    pageGap: PageGap | null;
    onOpenLine: (rid: string, group: TimeGroup) => void;
    /** Opens the line that carries the pages (G1). */
    onShowPageLine: () => void;
    onAdd: () => void;
}

export function PlanCard({
    groups,
    lineCount,
    counts,
    term,
    month,
    notStarted,
    start,
    changes,
    refused,
    warnings,
    onDismissWarnings,
    changeLog,
    pageGap,
    onOpenLine,
    onShowPageLine,
    onAdd,
}: PlanCardProps) {
    const pageLabel = pageGap?.rid ? groups.flatMap((g) => g.rows).find((r) => r.line.rid === pageGap.rid)?.line.label.trim() || null : null;
    return (
        <FoldCard
            id="plan"
            anchor="the-plan"
            title="The plan"
            icon={{ icon: CalendarIcon, area: "dates" }}
            // The button alone, and its plus alone on a phone, so the head stays
            // one line: "Month 2 of 12" leads the key line instead, and the
            // right slot never wraps onto a row of its own.
            right={(
                <PillButton size="sm" icon={PlusIcon} title="Add a line" className={cn(TAP_TARGET, "max-sm:w-7 max-sm:px-0")} onClick={onAdd}>
                    <span className="sr-only sm:not-sr-only">Add a line</span>
                </PillButton>
            )}
            foldedFact={
                changes > 0 ? (
                    <span className="tabular-nums text-amber-700">{plural(changes, "change")} not saved</span>
                ) : (
                    <span className="tabular-nums">{plural(lineCount, "line")}</span>
                )
            }
        >
            {lineCount === 0 ? (
                <p className={cn(TYPE.tb, "m-0 text-gray-500")}>No lines yet.</p>
            ) : (
                <>
                    <KeyLine when={notStarted ? "Not started" : `Month ${month} of ${term}`} total={lineCount} checked={counts.checked} confirmed={counts.confirmed} />
                    {pageGap && <PagesOff gap={pageGap} label={pageLabel} onShow={onShowPageLine} />}
                    {warnings.length > 0 && <Warnings warnings={warnings} onDismiss={onDismissWarnings} />}
                    {/* The groups are the only children of this box, so the first loses its divider. */}
                    <div>
                        {groups.map((g) => (
                            <TimeGroupFold
                                key={`${g.id}:${groupStartsOpen(g) ? "open" : "folded"}`}
                                group={g}
                                start={start}
                                refused={refused}
                                pageRid={pageGap?.rid ?? null}
                                onOpenLine={onOpenLine}
                            />
                        ))}
                        {changeLog.length > 0 && <Changes log={changeLog} />}
                    </div>
                </>
            )}
        </FoldCard>
    );
}

/**
 * Where the term stands, then the legend, and the only place the two counts
 * appear. "Of 20 lines" scopes them to the whole plan, so "7" never reads as
 * the 7 rows of the open group.
 */
function KeyLine({ when, total, checked, confirmed }: { when: string; total: number; checked: number; confirmed: number }) {
    const dot = (
        <span aria-hidden className="text-gray-400">
            ·
        </span>
    );
    return (
        <p className={cn(TYPE.tc, "m-0 mb-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-gray-600")}>
            <span className="whitespace-nowrap tabular-nums text-gray-900">{when}</span>
            {dot}
            <span className="whitespace-nowrap tabular-nums">Of {plural(total, "line")}:</span>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <CoversIcon covered />
                site scan checks <span className="tabular-nums">{checked}</span>
            </span>
            {/* The icons part the two counts: no dot left hanging at a line's end on a phone. */}
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap pl-1.5">
                <CoversIcon covered={false} />
                we confirm <span className="tabular-nums">{confirmed}</span>
            </span>
        </p>
    );
}

/**
 * The page sum the server refuses on, said before anyone presses Save: it
 * counts G1's monthly quantity against the pages the contract sells, on every
 * save that sends the lines, whichever line changed.
 */
function PagesOff({ gap, label, onShow }: { gap: PageGap; label: string | null; onShow: () => void }) {
    const line = label ? `“${label}”` : "The page line";
    const sum =
        gap.sold === 0
            ? // Plain words when the contract sells none (Angel, 15 Oct): which side to change.
              `The contract sells no pages, but ${line === "The page line" ? "the page line" : line} plans ${gap.implied}${gap.perMonth !== null ? ` (${gap.perMonth} a month for ${plural(gap.months, "month")})` : ""}. Record the pages the contract sells, or take the line out.`
            : gap.rid === null
            ? `No line gives pages, and the contract sells ${gap.sold}.`
            : gap.perMonth === null
                ? `${line} has no number in its quantity, so it gives 0 pages. The contract sells ${gap.sold}.`
                : `${line} gives ${gap.implied} (${gap.perMonth} a month × ${plural(gap.months, "month")}). The contract sells ${gap.sold}.`;
    return (
        <div className="mb-2 mt-2 rounded-2xl bg-amber-50 px-4 py-3 text-amber-800 ring-1 ring-inset ring-amber-200">
            <div className={cn(TYPE.tc, "min-w-0 space-y-1 break-words")}>
                <p className="m-0 font-medium">Pages do not add up</p>
                <p className="m-0 tabular-nums">
                    {sum} Oura refuses a save until the two match.
                    {gap.rid !== null && (
                        <>
                            {" "}
                            <PillButton variant="text" onClick={onShow}>
                                Show the line
                            </PillButton>
                        </>
                    )}
                </p>
            </div>
        </div>
    );
}

/** The server's warnings after a save, verbatim, until dismissed or the next save. */
function Warnings({ warnings, onDismiss }: { warnings: string[]; onDismiss: () => void }) {
    return (
        <div className="mb-2 mt-2 flex items-start gap-3 rounded-2xl bg-amber-50 px-4 py-3 text-amber-800 ring-1 ring-inset ring-amber-200">
            <div className={cn(TYPE.tc, "min-w-0 flex-1 space-y-1 break-words")}>
                <p className="m-0 font-medium">Saved, with {plural(warnings.length, "warning")}</p>
                {warnings.map((w, i) => (
                    <p key={i} className="m-0">
                        {w}
                    </p>
                ))}
            </div>
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss the warnings"
                className={cn("relative grid h-6 w-6 shrink-0 place-items-center rounded-full text-amber-700 hover:bg-amber-100", TAP_TARGET, FOCUS_RING)}
            >
                <XIcon aria-hidden size={14} />
            </button>
        </div>
    );
}

const GROUP_TONE: Record<ViewGroup["state"], Tone> = {
    current: "blue",
    past: "gray",
    future: "gray",
    not_started: "gray",
    none: "red",
};

/**
 * Where a group sits in time, on its head open or folded: "This month", "Due
 * by Thu 17 Sep" (its window closed; the plan has no done state, so a line in
 * it may have slipped, and "Ended" read as finished), or "Next: month 6".
 */
function groupWhen(g: ViewGroup, start: string): { tone: Tone; text: string } | null {
    if (g.state === "current") return { tone: "blue", text: "This month" };
    if (g.state === "past" && g.endMonth !== null) {
        const day = monthEndDay(start, g.endMonth);
        return { tone: "gray", text: day ? `Due by ${day}` : `Due in month ${g.endMonth}` };
    }
    if ((g.state === "future" || g.state === "not_started") && g.nextMonth !== null) return { tone: "gray", text: `Next: month ${g.nextMonth}` };
    return null;
}

function TimeGroupFold({
    group: g,
    start,
    refused,
    pageRid,
    onOpenLine,
}: {
    group: ViewGroup;
    start: string;
    refused: ReadonlySet<string>;
    pageRid: string | null;
    onOpenLine: PlanCardProps["onOpenLine"];
}) {
    const { unsaved, toFix } = groupCounts(g, refused);
    const pagesHere = pageRid !== null && g.rows.some((r) => r.line.rid === pageRid);
    const when = groupWhen(g, start);
    // A fold never hides what needs acting on.
    const note = [
        unsaved > 0 && `${unsaved} not saved`,
        toFix > 0 && <span className={LATE_TEXT}>{toFix} to fix</span>,
        toFix === 0 && pagesHere && <span className={LATE_TEXT}>Pages to fix</span>,
    ].filter(Boolean) as ReactNode[];
    return (
        <GroupFold
            id={g.id}
            lead="icon"
            tone={GROUP_TONE[g.state]}
            title={g.title}
            count={g.rows.length}
            defaultFolded={!groupStartsOpen(g)}
            // The month decides what is open, every visit: a hand fold is not kept.
            remember={false}
            note={note.length > 0 && <span className="tabular-nums text-gray-500">{dotted(note)}</span>}
            tags={when ? <Tag tone={when.tone}>{when.text}</Tag> : undefined}
            wrap
        >
            {g.subs.map((s) => (
                <div key={s.key}>
                    {s.title && <p className={cn(TYPE.lab, "m-0 pb-1 pl-[26px] pt-3")}>{s.title}</p>}
                    <div>
                        {s.rows.map((r) => (
                            <PlanRow
                                key={r.line.rid}
                                row={r}
                                group={g}
                                refused={refused.has(r.line.rid)}
                                pagesOff={r.line.rid === pageRid}
                                onOpen={() => onOpenLine(r.line.rid, g.key)}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </GroupFold>
    );
}

function PlanRow({ row, group, refused, pagesOff, onOpen }: { row: ViewRow; group: ViewGroup; refused: boolean; pagesOff: boolean; onOpen: () => void }) {
    const l = row.line;
    const covered = isCovered(l);
    const label = l.label.trim();
    const quantity = rowQuantity(label, l.quantityText);
    const short = quantity && shortQuantity(quantity);
    const months = rowMonthsFact(row, group);
    const unsaved = row.isNew ? "New" : row.edited ? "Edited" : null;
    const muted = group.state === "current" && !row.runsNow;
    const parts: ReactNode[] = [];
    if (unsaved) parts.push(<span className="text-amber-700">{unsaved}</span>);
    if (short) parts.push(<span title={short !== quantity ? quantity : undefined}>{short}</span>);
    if (months) parts.push(<span className="tabular-nums">{months}</span>);
    // A late note takes the fact's place, as ListRow's own `late` does.
    const late = rowLate(row, refused, pagesOff);
    const fact = late ? (
        <span className={cn(TYPE.tc, LATE_TEXT, "whitespace-nowrap")}>{late}</span>
    ) : parts.length > 0 ? (
        <span className={cn(TYPE.tc, muted ? "text-gray-400" : "text-gray-500")}>{dotted(parts)}</span>
    ) : null;
    return (
        <ListRow
            lead="icon"
            stackMeta
            onClick={onOpen}
            data-plan-line={l.rid}
            icon={<CoversMark line={l} />}
            title={(
                <>
                    {label || "No label yet"}
                    {/* Right after the label, as on the Documents board: the codes
                        line up with what they name, not with a fact's varying width. */}
                    <span className="ml-2 inline-flex align-[1px]">
                        <FamilyChip family={familyOf(l.code)}>{l.code}</FamilyChip>
                    </span>
                </>
            )}
            srStatus={`${covered ? COVERED_WORDS : BY_HAND_WORDS}${unsaved ? ", not saved" : ""}`}
            tags={fact ?? undefined}
            muted={muted}
        />
    );
}

const CHANGES_SHOWN = 3;

/** Who changed the plan and why, newest first. The server appends; the fixtures prepend. */
function Changes({ log }: { log: EngagementChange[] }) {
    const sorted = [...log].sort((a, b) => (b.at ?? "").localeCompare(a.at ?? ""));
    const last = sorted[0];
    const lastWhen = last ? dayDate(last.at) : null;
    const row = (c: EngagementChange, i: number) => (
        <ListRow
            key={`${c.at}-${i}`}
            lead="icon"
            stackMeta
            icon={<PenLineIcon aria-hidden size={14} strokeWidth={2} className={AREA_TEXT.dates} />}
            title={c.summary}
            meta={[c.by, shortDateOrNull(c.at)].filter(Boolean).join(" · ")}
        />
    );
    return (
        <GroupFold
            id="plan.changes"
            lead="icon"
            tone="gray"
            // A pen, not a dot: the log is not one more period of the plan.
            mark={<PenLineIcon aria-hidden size={14} strokeWidth={2} className={AREA_TEXT.dates} />}
            title="Changes"
            count={log.length}
            defaultFolded
            note={last && <span className="text-gray-500">Last: {[last.by, lastWhen].filter(Boolean).join(" · ")}</span>}
            wrap
        >
            <div>{sorted.slice(0, CHANGES_SHOWN).map(row)}</div>
            <ShowMore count={sorted.length - CHANGES_SHOWN}>{sorted.slice(CHANGES_SHOWN).map((c, i) => row(c, i + CHANGES_SHOWN))}</ShowMore>
        </GroupFold>
    );
}
